import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Loader2, X, Search, Pin } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { ChancesOverTimeResponse, ChancesSnapshot } from '../types/types';
import { getChancesOverTime } from '../api/api';
import { useConfig } from '../context/ConfigContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';

const MAX_VISIBLE = 10;
const DEFAULT_SELECTED = 6;

// 10 distinct colors for player lines
const PLAYER_COLORS = [
  '#2563eb', // blue
  '#dc2626', // red
  '#16a34a', // green
  '#9333ea', // purple
  '#ea580c', // orange
  '#0891b2', // cyan
  '#db2777', // pink
  '#ca8a04', // yellow
  '#4f46e5', // indigo
  '#059669', // emerald
];

interface ChancesOverTimeProps {
  isOpen: boolean;
  onClose: () => void;
  playerPicks: string;
  activePoolName?: string | null;
  pinnedPlayers?: string[];
}

interface ChartDataPoint {
  gameIndex: number;
  roundName: string | null;
  advancesTo: string | null;
  winner: string | null;
  loser: string | null;
  winnerSeed: number | null;
  loserSeed: number | null;
  [playerName: string]: number | string | null;
}

// Compute round regions: start gameIndex, end gameIndex, and label
// Uses advancesTo so labels reflect the round winners advance into
function getRoundRegions(snapshots: ChancesSnapshot[]): { start: number; end: number; mid: number; label: string }[] {
  const regions: { start: number; end: number; mid: number; label: string }[] = [];
  let currentLabel: string | null = null;
  let regionStart = 0;

  for (const snap of snapshots) {
    if (snap.advancesTo && snap.advancesTo !== currentLabel) {
      if (currentLabel) {
        const end = snap.gameIndex;
        regions.push({ start: regionStart, end, mid: (regionStart + end) / 2, label: currentLabel });
      }
      currentLabel = snap.advancesTo;
      regionStart = snap.gameIndex;
    }
  }
  // Drop Championship Game — too narrow at the end of the chart
  return regions.slice(0, -1);
}

const ChancesOverTime: React.FC<ChancesOverTimeProps> = ({
  isOpen,
  onClose,
  playerPicks,
  activePoolName,
  pinnedPlayers = [],
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ChancesOverTimeResponse | null>(null);
  const [selectedPlayers, setSelectedPlayers] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [logScale, setLogScale] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { roundConfigs, currentPointValues } = useConfig();

  // Reset state when pool changes
  useEffect(() => {
    setData(null);
    setError(null);
    setLoading(false);
    setSelectedPlayers(new Set());
  }, [activePoolName, playerPicks]);

  const roundNames = useMemo(
    () => ["Round of 64", ...roundConfigs.map(c => c.name)],
    [roundConfigs]
  );
  const pointsPerRound = currentPointValues;

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        searchRef.current &&
        !searchRef.current.contains(e.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Fetch data when dialog opens — skip if already loading or data is present
  useEffect(() => {
    if (!isOpen || !playerPicks.trim()) return;
    if (loading || data) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await getChancesOverTime(
          playerPicks,
          roundNames,
          pointsPerRound,
          activePoolName || undefined,
        );
        setData(result);
      } catch (err: any) {
        setError("This is going to take a while. Come on back in a few hours and we'll have this chart ready for you.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Auto-select: pinned players first, then fill with top N by final probability
  useEffect(() => {
    if (!data || data.snapshots.length === 0) return;

    const lastSnap = data.snapshots[data.snapshots.length - 1];
    const allNames = new Set(lastSnap.playerResults.map(p => p.name));

    // Start with pinned players that exist in the data
    const initial = new Set<string>();
    for (const name of pinnedPlayers) {
      if (allNames.has(name) && initial.size < DEFAULT_SELECTED) {
        initial.add(name);
      }
    }

    // Fill remaining slots with top players by probability.
    // Walk backwards through snapshots to find players with non-zero probability,
    // so we don't just pick players who are all at 0% in a finished tournament.
    const slotsToFill = DEFAULT_SELECTED - initial.size;
    if (slotsToFill > 0) {
      for (let si = data.snapshots.length - 1; si >= 0; si--) {
        const snap = data.snapshots[si];
        const candidates = [...snap.playerResults]
          .filter(p => !initial.has(p.name) && p.probabilityPercentage > 0)
          .sort((a, b) => b.probabilityPercentage - a.probabilityPercentage);
        for (const p of candidates) {
          if (initial.size >= DEFAULT_SELECTED) break;
          initial.add(p.name);
        }
        if (initial.size >= DEFAULT_SELECTED) break;
      }
    }

    setSelectedPlayers(initial);
  }, [data, pinnedPlayers]);

  // Build chart data
  const { chartData, allPlayers } = useMemo(() => {
    if (!data) return { chartData: [], allPlayers: [] };

    const allPlayersSet = new Set<string>();
    data.snapshots.forEach(snap =>
      snap.playerResults.forEach(pr => allPlayersSet.add(pr.name))
    );
    const allPlayers = Array.from(allPlayersSet).sort();

    const chartData: ChartDataPoint[] = data.snapshots.map(snap => {
      const point: ChartDataPoint = {
        gameIndex: snap.gameIndex,
        roundName: snap.roundName,
        advancesTo: snap.advancesTo,
        winner: snap.winner,
        loser: snap.loser,
        winnerSeed: snap.winnerSeed,
        loserSeed: snap.loserSeed,
      };
      snap.playerResults.forEach(pr => {
        point[pr.name] = Math.round(pr.probabilityPercentage * 10) / 10;
      });
      return point;
    });

    return { chartData, allPlayers };
  }, [data]);

  // For log scale, replace 0 with the domain floor so points render at the bottom
  const displayData = useMemo(() => {
    if (!logScale) return chartData;
    return chartData.map(point => {
      const adjusted: ChartDataPoint = { ...point };
      for (const name of allPlayers) {
        if (adjusted[name] === 0) {
          adjusted[name] = 0.1;
        }
      }
      return adjusted;
    });
  }, [chartData, allPlayers, logScale]);

  const roundRegions = useMemo(
    () => (data ? getRoundRegions(data.snapshots) : []),
    [data]
  );

  // Find the elimination game for each player: first snapshot where they hit 0%
  // and never recover
  const eliminationMap = useMemo(() => {
    if (!data || chartData.length === 0) return new Map<string, ChartDataPoint>();
    const map = new Map<string, ChartDataPoint>();

    for (const name of allPlayers) {
      for (let i = 1; i < chartData.length; i++) {
        const val = chartData[i][name] as number | null;
        if (val !== null && val === 0) {
          // Check they never recover
          const recovers = chartData.slice(i + 1).some(pt => {
            const v = pt[name] as number | null;
            return v !== null && v > 0;
          });
          if (!recovers) {
            map.set(name, chartData[i]);
            break;
          }
        }
      }
    }
    return map;
  }, [data, chartData, allPlayers]);

  // Compute Y axis max from selected players' data
  const yMax = useMemo(() => {
    if (chartData.length === 0 || selectedPlayers.size === 0) return 100;
    let max = 0;
    for (const point of chartData) {
      for (const name of Array.from(selectedPlayers)) {
        const val = point[name] as number | null;
        if (val !== null && val > max) max = val;
      }
    }
    // Round up to nearest nice number with some headroom
    if (max <= 5) return Math.ceil(max + 1);
    if (max <= 20) return Math.ceil(max / 5) * 5 + 5;
    return Math.min(100, Math.ceil(max / 10) * 10 + 10);
  }, [chartData, selectedPlayers]);

  // Assign colors to selected players (stable within a selection)
  const playerColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    const selected = Array.from(selectedPlayers).sort();
    selected.forEach((name, i) => {
      map[name] = PLAYER_COLORS[i % PLAYER_COLORS.length];
    });
    return map;
  }, [selectedPlayers]);

  const addPlayer = (name: string) => {
    setSelectedPlayers(prev => {
      if (prev.size >= MAX_VISIBLE || prev.has(name)) return prev;
      const next = new Set(prev);
      next.add(name);
      return next;
    });
    setSearchQuery('');
    setDropdownOpen(false);
  };

  const removePlayer = (name: string) => {
    setSelectedPlayers(prev => {
      const next = new Set(prev);
      next.delete(name);
      return next;
    });
  };

  // Filter dropdown results
  const dropdownResults = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return allPlayers
      .filter(name => !selectedPlayers.has(name))
      .filter(name => !q || name.toLowerCase().includes(q))
      .slice(0, 20);
  }, [allPlayers, selectedPlayers, searchQuery]);

  const formatGameDescription = (point: ChartDataPoint): string => {
    if (point.gameIndex === 0) return 'Pre-tournament';
    if (!point.winner) return `Game ${point.gameIndex}`;
    const ws = point.winnerSeed ? `(${point.winnerSeed}) ` : '';
    const ls = point.loserSeed ? `(${point.loserSeed}) ` : '';
    const loserPart = point.loser ? ` over ${ls}${point.loser}` : '';
    const advPart = point.advancesTo ? ` \u2192 ${point.advancesTo}` : '';
    return `${ws}${point.winner}${loserPart}${advPart}`;
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    const point = chartData.find(d => d.gameIndex === label);
    return (
      <div className="bg-background border border-border rounded-lg shadow-lg p-3 text-sm max-w-[300px]">
        <p className="font-semibold text-foreground mb-1">
          {point ? formatGameDescription(point) : `Game ${label}`}
        </p>
        <div className="space-y-0.5">
          {payload
            .sort((a: any, b: any) => (b.value ?? 0) - (a.value ?? 0))
            .map((entry: any) => {
              const realVal = point ? point[entry.dataKey] as number ?? entry.value : entry.value;
              return (
                <div key={entry.dataKey} className="flex justify-between gap-3">
                  <span style={{ color: entry.color }}>{entry.dataKey}</span>
                  <span className="font-mono text-foreground">{realVal}%</span>
                </div>
              );
            })}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={open => { if (!open) onClose(); }}>
      <DialogContent
        className="sm:max-w-[1100px] max-h-[90vh] flex flex-col p-0 gap-0"
        onInteractOutside={e => { if (loading) e.preventDefault(); }}
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="text-2xl font-semibold">
            Win Probability Timeline
          </DialogTitle>
          <DialogDescription>
            Win probability for each player as tournament results came in.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-muted-foreground">
                Running simulations...
              </p>
              <p className="text-xs text-muted-foreground">
                For very large pools, this processing can take over an hour. If it seems stuck, please email us at <a href="mailto:hello@mybracketsim.com" className="underline">hello@mybracketsim.com</a>
                <br /><br />
                No need for you to stick around, we're building your chart now, so please check back in a bit!
              </p>
            </div>
          )}

          {error && (
            <div className="text-destructive p-4 bg-destructive/10 border border-destructive/30 rounded-md">
              {error}
            </div>
          )}

          {data && !loading && data.computing && data.snapshots.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-muted-foreground">
                Building your chart...
              </p>
              <p className="text-xs text-muted-foreground">
                We're crunching the numbers in the background. Close this window and check back in a bit — your chart will be waiting for you.
              </p>
            </div>
          )}

          {data && !loading && data.snapshots.length > 0 && (
            <>
              {/* Player selector */}
              <div className="mb-4">
                {/* Pinned players section */}
                {(() => {
                  const pinnedSelected = Array.from(selectedPlayers).filter(n => pinnedPlayers.includes(n)).sort();
                  if (pinnedSelected.length === 0) return null;
                  return (
                    <div className="mb-2">
                      <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">Pinned Players</div>
                      <div className="flex flex-wrap gap-1.5">
                        {pinnedSelected.map(name => (
                          <span
                            key={name}
                            className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full text-xs font-medium text-white"
                            style={{ backgroundColor: playerColorMap[name] }}
                          >
                            <Pin className="h-3 w-3 fill-white/70" />
                            {name}
                            <button
                              onClick={() => removePlayer(name)}
                              className="ml-0.5 rounded-full p-0.5 hover:bg-white/20 transition-colors"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Selected players section */}
                {(() => {
                  const nonPinned = Array.from(selectedPlayers).filter(n => !pinnedPlayers.includes(n)).sort();
                  if (nonPinned.length === 0) return null;
                  return (
                    <div className="mb-2">
                      <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">Selected Players</div>
                      <div className="flex flex-wrap gap-1.5">
                        {nonPinned.map(name => (
                          <span
                            key={name}
                            className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full text-xs font-medium text-white"
                            style={{ backgroundColor: playerColorMap[name] }}
                          >
                            {name}
                            <button
                              onClick={() => removePlayer(name)}
                              className="ml-0.5 rounded-full p-0.5 hover:bg-white/20 transition-colors"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Search input + dropdown */}
                {selectedPlayers.size < MAX_VISIBLE && (
                  <div className="relative">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                      <input
                        ref={searchRef}
                        type="text"
                        placeholder={`Search for a player to add (${MAX_VISIBLE - selectedPlayers.size} remaining)...`}
                        value={searchQuery}
                        onChange={e => {
                          setSearchQuery(e.target.value);
                          setDropdownOpen(true);
                        }}
                        onFocus={() => setDropdownOpen(true)}
                        className="w-full sm:w-[360px] h-8 pl-8 pr-3 text-sm rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                      />
                    </div>

                    {dropdownOpen && dropdownResults.length > 0 && (
                      <div
                        ref={dropdownRef}
                        className="absolute z-50 mt-1 w-full sm:w-[360px] max-h-48 overflow-y-auto rounded-md border border-border bg-background shadow-lg"
                      >
                        {dropdownResults.map(name => (
                          <button
                            key={name}
                            onClick={() => addPlayer(name)}
                            className="w-full text-left px-3 py-1.5 text-sm text-foreground hover:bg-muted transition-colors flex items-center gap-2"
                          >
                            {pinnedPlayers.includes(name) && (
                              <Pin className="h-3 w-3 text-warning fill-warning flex-shrink-0" />
                            )}
                            {name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Log scale toggle */}
              <div className="flex items-center gap-2 mb-2">
                <button
                  onClick={() => setLogScale(prev => !prev)}
                  className={`px-2.5 py-1 text-xs font-medium rounded border transition-colors ${
                    logScale
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-muted/50 text-muted-foreground border-border hover:bg-muted'
                  }`}
                >
                  Log scale
                </button>
                {logScale && (
                  <span className="text-xs text-muted-foreground">Logarithmic Y-axis — emphasizes small changes</span>
                )}
              </div>

              {/* Chart */}
              <div className="h-[400px] sm:h-[450px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={displayData} margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis
                      dataKey="gameIndex"
                      type="number"
                      domain={[0, 'dataMax']}
                      ticks={roundRegions.map(r => r.mid)}
                      tickFormatter={(val: number) => {
                        const region = roundRegions.find(r => Math.abs(r.mid - val) < 0.5);
                        return region ? region.label : '';
                      }}
                      tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      scale={logScale ? 'log' : 'auto'}
                      domain={logScale ? [0.1, yMax] : [0, yMax]}
                      allowDataOverflow
                      tickFormatter={(val: number) => `${val}%`}
                      className="text-xs"
                      width={50}
                    />
                    <Tooltip content={<CustomTooltip />} />

                    {/* Round boundary lines */}
                    {roundRegions.map(r => (
                      <ReferenceLine
                        key={r.start}
                        x={r.start}
                        stroke="hsl(var(--muted-foreground))"
                        strokeDasharray="4 4"
                        strokeOpacity={0.3}
                      />
                    ))}

                    {/* Player lines */}
                    {allPlayers
                      .filter(name => selectedPlayers.has(name))
                      .map(name => (
                        <Line
                          key={name}
                          type="monotone"
                          dataKey={name}
                          stroke={playerColorMap[name]}
                          strokeWidth={2}
                          dot={false}
                          activeDot={{ r: 4 }}
                          connectNulls
                        />
                      ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Elimination callouts for selected players */}
              {(() => {
                const eliminated = Array.from(selectedPlayers)
                  .sort()
                  .filter(name => eliminationMap.has(name));
                if (eliminated.length === 0) return null;
                return (
                  <div className="mt-4 space-y-1">
                    {eliminated.map(name => {
                      const point = eliminationMap.get(name)!;
                      return (
                        <div key={name} className="flex items-center gap-2 text-sm">
                          <span
                            className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: playerColorMap[name] }}
                          />
                          <span className="text-foreground font-medium">{name}</span>
                          <span className="text-muted-foreground">
                            eliminated by: {formatGameDescription(point)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Stats footer */}
              <div className="mt-4 text-xs text-muted-foreground text-center">
                <span>{data.totalSnapshots} games analyzed</span>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ChancesOverTime;
